import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, CheckCircle, XCircle, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface W9UploadProps {
  mentorId: string;
  currentStatus?: string;
  currentFileUrl?: string | null;
  onStatusUpdate?: (status: string) => void;
}

export const W9Upload: React.FC<W9UploadProps> = ({ 
  mentorId, 
  currentStatus = 'pending',
  currentFileUrl,
  onStatusUpdate 
}) => {
  const [uploading, setUploading] = useState(false);
  const [w9Status, setW9Status] = useState(currentStatus);
  const [fileUrl, setFileUrl] = useState(currentFileUrl);
  const { toast } = useToast();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'submitted':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'submitted':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      const file = event.target.files?.[0];
      
      if (!file) return;

      console.log('W-9 Upload attempt:', { mentorId, fileType: file.type, fileSize: file.size });

      // Validate file type
      if (!file.type.includes('pdf') && !file.type.includes('image')) {
        toast({
          title: "Invalid file type",
          description: "Please upload a PDF or image file.",
          variant: "destructive",
        });
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload a file smaller than 5MB.",
          variant: "destructive",
        });
        return;
      }

      // Delete old file if exists and extract the file path from URL
      if (fileUrl) {
        try {
          // Extract the file path from the full URL
          const urlParts = fileUrl.split('/');
          const bucketIndex = urlParts.findIndex(part => part === 'w9-documents');
          if (bucketIndex !== -1 && bucketIndex < urlParts.length - 1) {
            const filePath = urlParts.slice(bucketIndex + 1).join('/');
            console.log('Attempting to delete old file:', filePath);
            await supabase.storage
              .from('w9-documents')
              .remove([filePath]);
          }
        } catch (deleteError) {
          console.warn('Error deleting old file:', deleteError);
          // Continue with upload even if delete fails
        }
      }

      // Upload new file
      const fileExt = file.name.split('.').pop();
      const fileName = `w9-${Date.now()}.${fileExt}`;
      const filePath = `${mentorId}/w9/${fileName}`;

      console.log('Uploading W-9 file:', { fileName, filePath, fileSize: file.size });

      const { error: uploadError } = await supabase.storage
        .from('w9-documents')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw uploadError;
      }

      // Store the file path for private bucket access
      const fullFilePath = filePath;

      console.log('File uploaded successfully, updating compliance record:', { fullFilePath });
      
      // Update mentor compliance record with file path (not public URL since bucket is private)
      const { error: updateError } = await supabase
        .from('mentor_compliance')
        .upsert({
          mentor_id: mentorId,
          w9_file_url: fullFilePath, // Store the file path for signed URL access
          w9_status: 'submitted',
          w9_submitted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, { onConflict: 'mentor_id' });

      if (updateError) {
        console.error('Database update error:', updateError);
        throw updateError;
      }

      setFileUrl(fullFilePath);
      setW9Status('submitted');
      onStatusUpdate?.('submitted');

      toast({
        title: "W-9 uploaded successfully",
        description: "Your W-9 form has been submitted for review.",
      });

    } catch (error) {
      console.error('Error uploading W-9:', error);
      toast({
        title: "Error uploading W-9",
        description: error instanceof Error ? error.message : "There was an error uploading your W-9 form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadW9 = async () => {
    if (!fileUrl) return;
    
    try {
      // Generate a signed URL for private bucket access
      const { data, error } = await supabase.storage
        .from('w9-documents')
        .createSignedUrl(fileUrl, 300); // 5 minute expiry
      
      if (error) {
        console.error('Error creating signed URL:', error);
        toast({
          title: "Error",
          description: "Failed to access W-9 file.",
          variant: "destructive",
        });
        return;
      }
      
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading W-9:', error);
      toast({
        title: "Error",
        description: "Failed to download W-9 file.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          W-9 Tax Form
        </CardTitle>
        <CardDescription>
          Upload your completed W-9 form to enable invoice creation and payouts.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Status:</span>
          <Badge className={getStatusColor(w9Status)}>
            {getStatusIcon(w9Status)}
            <span className="ml-1 capitalize">{w9Status}</span>
          </Badge>
        </div>

        {w9Status === 'pending' && (
          <Alert>
            <AlertDescription>
              You need to submit a W-9 form before you can create invoices for completed sessions.
            </AlertDescription>
          </Alert>
        )}

        {w9Status === 'rejected' && (
          <Alert className="border-red-200 bg-red-50">
            <AlertDescription className="text-red-800">
              Your W-9 was rejected. Please upload a corrected version or contact support.
            </AlertDescription>
          </Alert>
        )}

        {w9Status === 'submitted' && (
          <Alert className="border-yellow-200 bg-yellow-50">
            <AlertDescription className="text-yellow-800">
              Your W-9 is under review. You'll be notified once it's approved.
            </AlertDescription>
          </Alert>
        )}

        {w9Status === 'approved' && (
          <Alert className="border-green-200 bg-green-50">
            <AlertDescription className="text-green-800">
              Your W-9 has been approved! You can now create invoices for completed sessions.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          {fileUrl && (
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span className="text-sm">W-9 Form</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadW9}
              >
                Download
              </Button>
            </div>
          )}

          {(w9Status === 'pending' || w9Status === 'rejected') && (
            <div>
              <input
                type="file"
                id="w9-upload"
                accept=".pdf,image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
              />
              <label htmlFor="w9-upload">
                <Button
                  asChild
                  disabled={uploading}
                  className="w-full cursor-pointer"
                >
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? 'Uploading...' : fileUrl ? 'Replace W-9' : 'Upload W-9'}
                  </span>
                </Button>
              </label>
            </div>
          )}
        </div>

        <div className="text-xs text-gray-500 space-y-1">
          <p>• Accepted formats: PDF, JPG, PNG</p>
          <p>• Maximum file size: 5MB</p>
          <p>• Download blank W-9 form from <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">IRS.gov</a></p>
        </div>
      </CardContent>
    </Card>
  );
};